import { Task } from "@shopliftr/common-js/admin";

/**
 * Helpers for reasoning about a flyer task tree.
 */
export class TaskTreeUtil {

    /**
     * Time after `dateApproved` at which an Approved task is treated as failed publishing.
     */
    static readonly STUCK_AFTER_MS = 60 * 60 * 1000;

    /**
     * Finds the ancestors of a task within a tree, nearest first.
     *
     * @param task - The task whose ancestors are wanted
     * @param tree - Every task in the tree
     * @returns The ancestors, nearest first
     */
    static ancestorsOf(task: Task, tree: ReadonlyArray<Task>): Array<Task> {

        const byId = new Map(tree.map((t) => [t.id, t]));
        const ancestors: Array<Task> = [];
        const seen = new Set<string>([task.id]);
        let parentId = task.parentTask;

        while (parentId && !seen.has(parentId) && byId.has(parentId)) {
            const parent = byId.get(parentId);
            ancestors.push(parent);
            seen.add(parentId);
            parentId = parent.parentTask;
        }

        return ancestors;
    }

    /**
     * Splits Approved tasks into those still publishing and those stuck.
     *
     * @param tree - Every task in the tree
     * @param now - The current time
     * @returns The waiting and stuck tasks
     */
    static publishingTasks(tree: ReadonlyArray<Task>, now: Date): {
        waiting: Array<Task>;
        stuck: Array<Task>;
    } {

        const waiting: Array<Task> = [];
        const stuck: Array<Task> = [];

        tree.filter((t) => t.isApproved()).forEach((t) => {
            const approvedAt = t.dateApproved?.getTime();

            if (approvedAt !== undefined && now.getTime() - approvedAt > TaskTreeUtil.STUCK_AFTER_MS) {
                stuck.push(t);
            }
            else {
                waiting.push(t);
            }
        });

        return {
            waiting,
            stuck
        };
    }
}
